import React from 'react';
import PropTypes from 'prop-types';

import { Trans, useTranslation } from 'react-i18next';
import { Icon, WeezHeader, Card, ToastContext } from '@weezevent/nacre';
import { Progress } from 'semantic-ui-react';
import MinisiteEditContainer from './minisiteContainer';
import Minisite from '../../models/minisite';
import EventModel from '../../models/eventModel';
import Loading from '../../components/loading';
import Style from '../../styles/minisite/empty.module.css';
import WorldLogo from './weez-world.png';
import { getFromQueryParams } from '../attendees/manage/components/filters/queryParams';
import useMixpanel from '../../context/useMixpanel';

export default function Index({ event, organization, standalone, autoCreate }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [loading, setLoading] = React.useState(false);
    const [create, setCreate] = React.useState(false);
    const mixpanel = useMixpanel();

    const [myEvent, loadingEvent, syncEvent] = EventModel.useApiModel({ id: event.id, organizationId: organization.id, cache: false });

    // Allow autoCreate only if timestamp issued in the past 10 minutes
    // Prevent autoCreate of a minisite on simple reload of a tab in navigator
    // TODO : Remove when there is no more interaction with old Weez
    const canAutoCreate = React.useMemo(() => {
        const timestamp = getFromQueryParams('ts'); // Unix timestamp missing millisecond part
        if (timestamp) {
            const date = parseInt(timestamp) * 1000; // timestamp of the issued link
            const now = Date.now(); // timestamp of now
            const diff = now - date; // Diff in ms
            const diffMin = diff / 1000 / 60; // Diff in min
            if (diffMin < 10) {
                return true;
            }
        }
        return false;
    }, []);

    React.useEffect(() => {
        if (canAutoCreate && autoCreate && myEvent && !myEvent.site) {
            setCreate(true);
            // display the create card at least 1,5s
            setTimeout(() => setCreate(false), 1500);
            mixpanel.track('ACTION - Minisite > Create');
            generateSite();
        }
    }, [myEvent, autoCreate]);

    const generateSite = React.useCallback(() => {
        setLoading(true);
        const minisite = new Minisite();
        minisite
            .generate({ organizationId: organization.id, eventId: myEvent.id })
            .then(response => {
                const minisiteId = response.id;
                Minisite.get({ id: response.id, organizationId: organization.id, cache: false })
                    .then(responseMinisite => {
                        let eventObject = new EventModel({
                            id: myEvent.id,
                            site: minisiteId,
                            gemino_url: `https://${responseMinisite.domain}/${responseMinisite.slug}`
                        });
                        eventObject
                            .save({ organizationId: organization.id })
                            .then(() => {
                                toast.success(t(`ticket.event.minisite.toast.createSuccess`));
                                syncEvent();
                                setLoading(false);
                            })
                            .catch(e => {
                                toast.error(t(`ticket.event.minisite.toast.associateError`));
                                setLoading(false);
                            });
                    })
                    .catch(e => {
                        toast.error(t(`ticket.event.minisite.toast.createError`));
                        setLoading(false);
                    });
            })
            .catch(e => {
                toast.error(t(`ticket.event.minisite.toast.createError`));
                setLoading(false);
            });
    }, [organization, myEvent]);

    if (loadingEvent) {
        return <Loading />;
    }

    if (create || loading) {
        return (
            <div className={Style['container']}>
                <img src={WorldLogo} alt="Auto Create Minisite" className={Style['img']} />
                <Progress percent={100} indicating />
                <p>
                    <Trans i18nKey="ticket.event.minisite.autoCreate" />
                </p>
            </div>
        );
    }

    const currentEvent = myEvent && myEvent.site ? myEvent : event;

    if (currentEvent.site) {
        return <MinisiteEditContainer event={currentEvent} organization={organization} standalone={standalone} />;
    }

    return (
        <>
            <WeezHeader title={t('ticket.event.minisite.header.title')} />
            <Card>
                <Card.EmptyState
                    icon={<Icon name="desktop" />}
                    title={t('ticket.event.minisite.no-minisite.title')}
                    subtitle={t('ticket.event.minisite.no-minisite.subtitle')}
                    labelButton={t('ticket.event.minisite.no-minisite.cta')}
                    onClick={generateSite}
                />
            </Card>
        </>
    );
}

Index.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    standalone: PropTypes.bool,
    autoCreate: PropTypes.bool
};

Index.defaultProps = {
    standalone: false,
    autoCreate: true
};
